/**
 * Bidirectional mapper for current_level values (EN ↔ DE)
 * 
 * DB can contain either English or German values.
 * Frontend displays values based on user's selected language.
 */

/** Max length for comma-joined multi-select level labels (matches backend). */
export const CURRENT_LEVEL_MAX_LENGTH = 500;

export const joinLevelValues = (values: string[]): string => values.join(', ');

export const getJoinedLevelLength = (values: string[]): number =>
  joinLevelValues(values).length;

export const isLevelSelectionTooLong = (values: string[]): boolean =>
  getJoinedLevelLength(values) > CURRENT_LEVEL_MAX_LENGTH;

// English → German mapping
export const EN_TO_DE: Record<string, string> = {
  "High School Student": "Schüler:in / Abiturient:in",
  "Apprentice / Vocational Training": "Auszubildende:r / Azubi / Lehrling",
  "Bachelor / Undergraduate Student": "Bachelor-Student:in",
  "Master / Graduate Student": "Master-Student:in",
  "Working Student / Intern": "Werkstudent:in / Praktikant:in",
  "PhD Student": "Doktorand:in / PhD-Student:in",
  "Qualified Worker (after apprenticeship)": "Ausgebildete Fachkraft",
  "Entrepreneur / Self-Employed": "Unternehmer:in / Selbstständige",
  "Junior / Entry-Level / New Grad (0-2 years)": "Junior / Berufseinsteiger:in (0-2 Jahre)",
  "Mid-Level (2-5 years)": "Erfahren / Mid-Level (2-5 Jahre)",
  "Senior (5+ years)": "Senior (5+ Jahre)",
  "Master (Baker, Hairstylist, Carpenter, Electrician, etc.)": "Meister:in (Bäcker:in, Friseur:in, Tischler:in, Elektroniker:in, etc.)",
  "Lead / Management": "Führungskraft / Management / Teamleiter:in",
  "Expert / Principal (15+ years)": "Expert:in / Spezialist:in (15+ Jahre)"
};

// German → English mapping (auto-generated from EN_TO_DE)
export const DE_TO_EN: Record<string, string> = Object.entries(EN_TO_DE).reduce(
  (acc, [en, de]) => {
    acc[de] = en;
    return acc;
  },
  {} as Record<string, string>
);

/**
 * Maps a level value from one language to another
 * @param value - The level value to map
 * @param targetLang - The target language ('en' or 'de')
 * @returns The mapped value, or the original value if no mapping exists
 */
export const mapLevelValue = (value: string | null | undefined, targetLang: 'en' | 'de'): string | null => {
  if (!value) return null;
  
  if (targetLang === 'de') {
    // Map EN → DE
    return EN_TO_DE[value] || value;
  } else {
    // Map DE → EN
    return DE_TO_EN[value] || value;
  }
};

/**
 * Maps an array of level values from one language to another
 * @param values - Array of level values to map
 * @param targetLang - The target language ('en' or 'de')
 * @returns Array of mapped values
 */
export const mapLevelValues = (values: string[], targetLang: 'en' | 'de'): string[] => {
  return values.map(v => mapLevelValue(v, targetLang) || v);
};

/**
 * Detects if a value is in German or English
 * @param value - The level value to check
 * @returns 'de' if German, 'en' if English, 'unknown' if not found
 */
export const detectLevelLanguage = (value: string): 'en' | 'de' | 'unknown' => {
  if (EN_TO_DE[value]) return 'en';
  if (DE_TO_EN[value]) return 'de';
  return 'unknown';
};

